import { ValidationResult, ValidationError, StorageOptions } from "./types";

/**
 * Core validation system for storage operations
 */
export class ValidationSystem {
  /**
   * Validate data against provided options
   */
  validateData(data: unknown, options?: StorageOptions): ValidationResult {
    const errors: ValidationError[] = [];

    // Null check
    if (data === null || data === undefined) {
      errors.push({
        code: "NULL_DATA",
        message: "Data cannot be null or undefined",
      });
      return { isValid: false, errors };
    }

    // Size validation
    if (options?.validation?.maxSize) {
      const size = this.calculateSize(data);
      if (size > options.validation.maxSize) {
        errors.push({
          code: "SIZE_EXCEEDED",
          message: `Data size ${size} exceeds maximum ${options.validation.maxSize}`,
          metadata: { size, maxSize: options.validation.maxSize },
        });
      }
    }

    // Type validation
    if (options?.validation?.allowedTypes?.length) {
      const dataType = this.getDataType(data);
      if (!options.validation.allowedTypes.includes(dataType)) {
        errors.push({
          code: "INVALID_TYPE",
          message: `Data type ${dataType} not in allowed types: ${options.validation.allowedTypes.join(", ")}`,
          metadata: {
            type: dataType,
            allowedTypes: options.validation.allowedTypes,
          },
        });
      }
    }

    // Schema validation if provided
    if (options?.validation?.schema) {
      const schemaErrors = this.validateSchema(data, options.validation.schema);
      errors.push(...schemaErrors);
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate storage key
   */
  validateKey(key: string): ValidationResult {
    const errors: ValidationError[] = [];

    // Key presence
    if (!key) {
      errors.push({
        code: "EMPTY_KEY",
        message: "Storage key cannot be empty",
      });
    }

    // Key format
    if (typeof key !== "string") {
      errors.push({
        code: "INVALID_KEY_TYPE",
        message: "Storage key must be a string",
      });
    }

    // Key length
    if (key.length > 255) {
      errors.push({
        code: "KEY_TOO_LONG",
        message: "Storage key exceeds maximum length of 255 characters",
        metadata: { length: key.length },
      });
    }

    // Key characters
    if (!/^[a-zA-Z0-9_\-\.]+$/.test(key)) {
      errors.push({
        code: "INVALID_KEY_FORMAT",
        message: "Storage key contains invalid characters",
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Calculate approximate size of data in bytes
   */
  private calculateSize(data: unknown): number {
    try {
      const str = JSON.stringify(data);
      return str.length * 2; // Unicode characters
    } catch (error) {
      return 0; // Cannot calculate size
    }
  }

  /**
   * Get type of data for validation
   */
  private getDataType(data: unknown): string {
    if (Array.isArray(data)) return "array";
    if (data === null) return "null";
    if (data instanceof Date) return "date";
    return typeof data;
  }

  /**
   * Validate data against a schema
   */
  private validateSchema(data: unknown, schema: unknown): ValidationError[] {
    const errors: ValidationError[] = [];

    // Basic schema validation
    // TODO: Implement full schema validation
    // For now, just check if data matches basic type

    if (typeof schema === "string") {
      // Schema is a type string
      if (this.getDataType(data) !== schema) {
        errors.push({
          code: "SCHEMA_TYPE_MISMATCH",
          message: `Data type does not match schema type ${schema}`,
          metadata: {
            expectedType: schema,
            actualType: this.getDataType(data),
          },
        });
      }
    }

    return errors;
  }
}

// Export singleton instance
export const validationSystem = new ValidationSystem();
